import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Scales, House, Folders, Users, Clock, Warning, SignOut, Gear, Headset, User, CaretDown, Bell, Question, DotsThree, Plus,
  TrayArrowDown, Lightning, MagicWand, FileText, PaperPlaneTilt, Gavel, CheckCircle, Files, BellRinging,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { AvatarCircle } from "@/pages/Settings";
import NotificationsBell from "@/components/NotificationsBell";
import { useActivity } from "@/context/ActivityContext";

// 9-Step Workflow (linke Sidebar)
const workflowItems = [
  { to: "/app/dashboard", label: "Übersicht", icon: House, testid: "nav-dashboard", step: null },
  { to: "/app/eingang",   label: "Eingang",   icon: TrayArrowDown, testid: "nav-eingang", step: 1 },
  { to: "/app/akten",     label: "Akten",     icon: Folders, testid: "nav-akten", step: 2 },
  { to: "/app/wiedervorlagen", label: "Wiedervorlage", icon: BellRinging, testid: "nav-wiedervorlagen", step: 3 },
  { to: "/app/aktionen",  label: "Aktionen",  icon: Lightning, testid: "nav-aktionen", step: 4, accent: true },
  { to: "/app/versand",   label: "Versand",   icon: PaperPlaneTilt, testid: "nav-versand", step: 6 },
  { to: "/app/fristen",   label: "Fristen",   icon: Clock, testid: "nav-fristen", step: 7 },
  { to: "/app/verfahren", label: "Verfahren", icon: Gavel, testid: "nav-verfahren", step: 8 },
  { to: "/app/abschluss", label: "Abschluss", icon: CheckCircle, testid: "nav-abschluss", step: 9 },
];

const sekundaerItems = [
  { to: "/app/dokumente", label: "Dokumente", icon: Files, testid: "nav-dokumente" },
  { to: "/app/mandanten", label: "Mandanten", icon: Users, testid: "nav-mandanten" },
  { to: "/app/owi", label: "OWi-Verfahren", icon: Warning, testid: "nav-owi" },
];

// Mobile bottom-nav: 4 wichtigste + Mehr
const mobilePrimary = [
  { to: "/app/dashboard", label: "Start", icon: House, testid: "mobile-nav-dashboard" },
  { to: "/app/eingang",   label: "Eingang", icon: TrayArrowDown, testid: "mobile-nav-eingang" },
  { to: "/app/aktionen",  label: "Aktionen", icon: Lightning, testid: "mobile-nav-aktionen" },
  { to: "/app/akten",     label: "Akten", icon: Folders, testid: "mobile-nav-akten" },
];
const mobileMoreItems = [
  { to: "/app/versand",   label: "Versand",   icon: PaperPlaneTilt, testid: "mobile-more-versand" },
  { to: "/app/fristen",   label: "Fristen",   icon: Clock, testid: "mobile-more-fristen" },
  { to: "/app/wiedervorlagen", label: "Wiedervorlage", icon: BellRinging, testid: "mobile-more-wiedervorlagen" },
  { to: "/app/verfahren", label: "Verfahren", icon: Gavel, testid: "mobile-more-verfahren" },
  { to: "/app/abschluss", label: "Abschluss", icon: CheckCircle, testid: "mobile-more-abschluss" },
  { to: "/app/dokumente", label: "Dokumente", icon: Files, testid: "mobile-more-dokumente" },
  { to: "/app/mandanten", label: "Mandanten", icon: Users, testid: "mobile-more-mandanten" },
  { to: "/app/owi",       label: "OWi-Verfahren", icon: Warning, testid: "mobile-more-owi" },
  { to: "/app/settings",  label: "Einstellungen", icon: Gear, testid: "mobile-more-settings" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [creating, setCreating] = useState(false);

  const createAkte = async (art) => {
    if (creating) return;
    setCreating(true);
    try {
      const r = await api.post("/akten", { art });
      nav(`/app/akten/${r.data.id}?capture=1`);
    } catch {
      // toast nicht hier importiert — sonnerfreie Variante
      window.alert("Konnte Akte nicht anlegen — bitte später erneut versuchen");
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    let active = true;
    let objUrl;
    (async () => {
      if (!user?.avatar_path) { setAvatarUrl(null); return; }
      try {
        const res = await api.get("/auth/avatar", { responseType: "blob" });
        if (!active) return;
        objUrl = URL.createObjectURL(res.data);
        setAvatarUrl(objUrl);
      } catch { setAvatarUrl(null); }
    })();
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [user?.avatar_path]);

  const handleLogout = () => {
    logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]">
      {/* DESKTOP SIDEBAR — nur auf großen Desktops (>=1280px). Auf iPad + Mobile Bottom-Nav. */}
      <aside className="hidden xl:flex w-64 shrink-0 bg-white border-r border-[#E5E7EB] flex-col">
        <div className="p-6 border-b border-[#E5E7EB]">
          <Link to="/" className="flex items-center gap-2">
            <Scales weight="duotone" size={24} className="text-[#1A2238]" />
            <span className="font-serif text-xl tracking-tight">Lex<span className="text-[#D32F2F]">Auto</span></span>
          </Link>
          <p className="overline text-[#9CA3AF] mt-4 truncate">{user?.kanzlei_name}</p>
        </div>

        <nav className="flex-1 overflow-y-auto p-4">
          <p className="overline text-[#9CA3AF] px-3 mb-2">Workflow</p>
          <ul className="space-y-0.5 mb-6">
            {workflowItems.map(({ to, label, icon: Icon, testid, step, accent }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  data-testid={testid}
                  end={to === "/app/dashboard"}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                      isActive
                        ? "bg-[#1A2238] text-white"
                        : accent
                        ? "text-[#D32F2F] hover:bg-[#FEE2E2]"
                        : "text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#0A192F]"
                    }`
                  }
                >
                  <Icon size={18} weight="duotone" />
                  <span className="flex-1">{label}</span>
                  {step != null && <span className="text-[10px] font-mono text-[#9CA3AF] tabular-nums">{step}</span>}
                </NavLink>
              </li>
            ))}
          </ul>

          <p className="overline text-[#9CA3AF] px-3 mb-2">Stammdaten</p>
          <ul className="space-y-0.5">
            {sekundaerItems.map(({ to, label, icon: Icon, testid }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  data-testid={testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                      isActive
                        ? "bg-[#1A2238] text-white"
                        : "text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#0A192F]"
                    }`
                  }
                >
                  <Icon size={18} weight="duotone" />
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-[#E5E7EB]">
          <NavLink
            to="/app/settings"
            data-testid="nav-settings"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                isActive ? "bg-[#1A2238] text-white" : "text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#0A192F]"
              }`
            }
          >
            <Gear size={18} weight="duotone" /> Einstellungen
          </NavLink>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-[#E5E7EB]">
          <div className="w-full px-4 xl:px-8 h-14 xl:h-16 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-2 xl:hidden">
                <Scales weight="duotone" size={22} className="text-[#1A2238]" />
                <span className="font-serif text-lg tracking-tight">Lex<span className="text-[#D32F2F]">Auto</span></span>
              </Link>
              {/* Primary action: neue Akte (Dropdown: HFG oder OWi) */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    className="bg-[#1B2239] hover:bg-[#2C3859] text-white h-9 px-3 xl:px-4"
                    data-testid="topbar-new-akte-btn"
                  >
                    <Plus size={16} className="mr-1 xl:mr-2" />
                    <span className="hidden sm:inline">Akte anlegen</span>
                    <span className="sm:hidden">Anlegen</span>
                    <CaretDown size={12} className="ml-1 xl:ml-2 opacity-80" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <DropdownMenuLabel className="text-xs font-semibold text-[#1A2238]">
                    Neue Akte
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => createAkte("unfall")}
                    disabled={creating}
                    data-testid="topbar-new-akte-hfg"
                    className="cursor-pointer"
                  >
                    <FileText size={16} weight="duotone" className="mr-2 text-[#1B2239]" />
                    <div className="flex flex-col">
                      <span className="font-medium">HFG</span>
                      <span className="text-[11px] text-[#9CA3AF]">Haftpflichtgutachten</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => createAkte("owi")}
                    disabled={creating}
                    data-testid="topbar-new-akte-owi"
                    className="cursor-pointer"
                  >
                    <Warning size={16} weight="duotone" className="mr-2 text-[#D32F2F]" />
                    <div className="flex flex-col">
                      <span className="font-medium">OWi</span>
                      <span className="text-[11px] text-[#9CA3AF]">Ordnungswidrigkeit / Bußgeld</span>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="hidden xl:block flex-1" />

            {/* Globaler Activity-Indikator (Hintergrund-Tasks wie KI-Analyse) */}
            <ActivityPill />

            <div className="flex items-center gap-2">
              <NotificationsBell />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-3 pl-1 pr-2 xl:pr-3 py-1 rounded-full hover:bg-[#F3F4F6] transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    <AvatarCircle url={avatarUrl} name={user?.full_name} size={32} />
                    <div className="text-left hidden md:block">
                      <p className="text-sm font-medium leading-tight">{user?.full_name}</p>
                      <p className="text-xs text-[#9CA3AF] leading-tight truncate max-w-[160px]">{user?.kanzlei_name}</p>
                    </div>
                    <CaretDown size={14} className="text-[#9CA3AF] hidden md:block" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex items-center gap-3">
                      <AvatarCircle url={avatarUrl} name={user?.full_name} size={40} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{user?.full_name}</p>
                        <p className="text-xs text-[#9CA3AF] truncate">{user?.email}</p>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => nav("/app/settings?section=profil")} data-testid="menu-profil">
                    <User size={16} className="mr-2" weight="duotone" /> Mein Profil
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/app/settings?section=kanzlei")} data-testid="menu-kanzlei">
                    <Gear size={16} className="mr-2" weight="duotone" /> Kanzleidaten
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/app/settings?section=briefkopf")} data-testid="menu-briefkopf">
                    <FileText size={16} className="mr-2" weight="duotone" /> Briefkopf &amp; Fußzeile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/app/settings?section=support")} data-testid="menu-support">
                    <Headset size={16} className="mr-2" weight="duotone" /> Support kontaktieren
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/app/settings?section=hilfe")} data-testid="menu-hilfe">
                    <Question size={16} className="mr-2" weight="duotone" /> Hilfe &amp; FAQ
                  </DropdownMenuItem>
                  {user?.role === "superadmin" && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => nav("/app/admin")} data-testid="menu-admin" className="text-[#1A2238] font-medium">
                        <Scales size={16} className="mr-2" weight="duotone" /> Admin-Bereich
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout" className="text-[#D32F2F] focus:text-[#D32F2F]">
                    <SignOut size={16} className="mr-2" weight="duotone" /> Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <div className="w-full px-4 sm:px-6 xl:px-8 py-6 xl:py-8 flex-1 pb-24 xl:pb-8">
          <Outlet />
        </div>

        {/* MOBILE + TABLET BOTTOM NAVIGATION — App-Feel bis 1279px (inkl. iPad Landscape 1024px) */}
        <nav
          className="xl:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-lg border-t border-[#E5E7EB] shadow-[0_-2px_16px_rgba(10,25,47,0.10)]"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          data-testid="mobile-bottom-nav"
        >
          <ul className="grid grid-cols-5 h-16 sm:h-18 max-w-2xl mx-auto">
            {mobilePrimary.map(({ to, label, icon: Icon, testid }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  data-testid={testid}
                  end={to === "/app/dashboard"}
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center gap-1 h-full text-[11px] font-medium transition-all active:scale-95 ${
                      isActive ? "text-[#1A2238]" : "text-[#9CA3AF] hover:text-[#4B5563]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className={`relative flex items-center justify-center transition-all ${isActive ? "scale-110" : ""}`}>
                        {isActive && (
                          <span className="absolute -inset-1.5 rounded-full bg-[#1A2238]/8" aria-hidden />
                        )}
                        <Icon size={24} weight={isActive ? "fill" : "duotone"} className="relative" />
                      </span>
                      <span className="truncate max-w-full px-1">{label}</span>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
            <li>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex flex-col items-center justify-center gap-1 h-full w-full text-[11px] font-medium text-[#9CA3AF] hover:text-[#4B5563] active:scale-95 transition-transform"
                    data-testid="mobile-nav-more"
                  >
                    <DotsThree size={26} weight="bold" />
                    <span>Mehr</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" side="top" className="w-60 mb-2">
                  {mobileMoreItems.map(({ to, label, icon: Icon, testid }) => (
                    <DropdownMenuItem key={to} onClick={() => nav(to)} data-testid={testid}>
                      <Icon size={16} weight="duotone" className="mr-2" /> {label}
                    </DropdownMenuItem>
                  ))}
                  {user?.role === "superadmin" && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => nav("/app/admin")} data-testid="mobile-more-admin" className="text-[#1A2238] font-medium">
                        <Scales size={16} className="mr-2" weight="duotone" /> Admin-Bereich
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-[#D32F2F] focus:text-[#D32F2F]" data-testid="mobile-more-logout">
                    <SignOut size={16} className="mr-2" weight="duotone" /> Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </li>
          </ul>
        </nav>
      </main>
    </div>
  );
}


/** Topbar-Activity-Pill: zeigt laufende Hintergrund-Tasks (KI-Analyse, Schriftsatz-Gen, …) */
function ActivityPill() {
  const { jobs } = useActivity();
  if (!jobs || jobs.length === 0) return null;
  const top = jobs[jobs.length - 1];
  return (
    <div
      className="hidden xl:inline-flex items-center gap-2 h-8 px-3 rounded-full bg-[#1A2238]/5 border border-[#1A2238]/15 text-[#1A2238] text-xs font-medium"
      data-testid="topbar-activity-pill"
      title={jobs.map(j => j.label).join(" · ")}
    >
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D32F2F] opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#D32F2F]"></span>
      </span>
      <span className="truncate max-w-[260px]">{top.label}</span>
      {jobs.length > 1 && (
        <span className="ml-1 inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-[#1A2238] text-white text-[10px] font-semibold">
          +{jobs.length - 1}
        </span>
      )}
    </div>
  );
}

